import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Card } from 'primereact/card';
import { Calendar, CalendarChangeEvent } from 'primereact/calendar';
import { formatCurrency } from '../utils';
import api from '../api';

interface TopProduct {
    productId: number;
    productName: string;
    quantity: number;
    totalValue: number;
}

interface MonthlyOrder {
    month: string;
    count: number;
    value: number;
}

interface ClientAnalysis {
    clientId: number;
    clientName: string;
    totalOrders: number;
    totalValue: number;
    averageOrderValue: number;
    topProducts: TopProduct[];
    ordersByMonth: MonthlyOrder[];
}

const ClientOrderAnalysis = () => {
    const [loading, setLoading] = useState(true);
    const [clientAnalytics, setClientAnalytics] = useState<ClientAnalysis[]>([]);
    const [selectedClient, setSelectedClient] = useState<ClientAnalysis | null>(null);
    const [dateRange, setDateRange] = useState<Date[]>([]);
    const [orderTrendData, setOrderTrendData] = useState<any>(null);
    const [productPreferencesData, setProductPreferencesData] = useState<any>(null);

    useEffect(() => {
        loadAnalytics();
    }, [selectedClient, dateRange]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            console.log('Fetching client analytics data...');
            const params = {
                clientId: selectedClient?.clientId,
                startDate: dateRange[0]?.toISOString(),
                endDate: dateRange[1]?.toISOString()
            };

            const response = await api.get<ClientAnalysis[]>('/analytics/client-orders', { params });
            console.log('Client analytics data:', response.data);
            setClientAnalytics(response.data);
            
            if (response.data.length > 0) {
                const selectedClientData = selectedClient 
                    ? response.data.find((c) => c.clientId === selectedClient.clientId)
                    : response.data[0];

                if (selectedClientData) {
                    updateCharts(selectedClientData);
                }
            }
        } catch (error) {
            console.error('Error loading client analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateCharts = (clientData: ClientAnalysis) => {
        // Order Trends Chart
        setOrderTrendData({
            labels: clientData.ordersByMonth.map(m => m.month),
            datasets: [
                {
                    label: 'Order Count',
                    data: clientData.ordersByMonth.map(m => m.count),
                    fill: false,
                    borderColor: '#42A5F5',
                    tension: 0.4
                },
                {
                    label: 'Order Value',
                    data: clientData.ordersByMonth.map(m => m.value),
                    fill: false,
                    borderColor: '#FFA726',
                    tension: 0.4
                }
            ]
        });

        // Product Preferences Chart
        setProductPreferencesData({
            labels: clientData.topProducts.map(p => p.productName),
            datasets: [
                {
                    data: clientData.topProducts.map(p => p.totalValue),
                    backgroundColor: [
                        '#42A5F5',
                        '#66BB6A',
                        '#FFA726',
                        '#26C6DA',
                        '#7E57C2'
                    ]
                }
            ]
        });
    };

    const valueTemplate = (rowData: ClientAnalysis) => {
        return formatCurrency(rowData.totalValue);
    };

    const avgValueTemplate = (rowData: ClientAnalysis) => {
        return formatCurrency(rowData.averageOrderValue);
    };

    const handleDateRangeChange = (e: CalendarChangeEvent) => {
        setDateRange(e.value as Date[] || []);
    };

    if (loading) {
        return (
            <div className="card">
                <div className="border-round border-1 surface-border p-4">
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <span className="ml-2">Loading analysis...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <h5>Client Order Analysis</h5>
                    <div className="flex gap-3 mb-4">
                        <Calendar
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            selectionMode="range"
                            readOnlyInput
                            showIcon
                            placeholder="Date Range"
                            className="w-20rem"
                            touchUI
                        />
                    </div>

                    <DataTable
                        value={clientAnalytics}
                        paginator
                        rows={10}
                        loading={loading}
                        selectionMode="single"
                        selection={selectedClient}
                        onSelectionChange={(e) => {
                            const selected = e.value as ClientAnalysis;
                            setSelectedClient(selected);
                        }}
                        dataKey="clientId"
                        className="mb-4"
                        emptyMessage="No client data available"
                        responsiveLayout="scroll"
                    >
                        <Column field="clientName" header="Client" sortable />
                        <Column field="totalOrders" header="Total Orders" sortable />
                        <Column field="totalValue" header="Total Value" body={valueTemplate} sortable />
                        <Column field="averageOrderValue" header="Avg. Order Value" body={avgValueTemplate} sortable />
                    </DataTable>

                    {selectedClient && orderTrendData && productPreferencesData && (
                        <div className="grid">
                            <div className="col-12 lg:col-6">
                                <Card title="Order Trends">
                                    <Chart type="line" data={orderTrendData} options={{
                                        plugins: {
                                            legend: {
                                                labels: {
                                                    color: '#495057'
                                                }
                                            }
                                        },
                                        scales: {
                                            x: {
                                                ticks: {
                                                    color: '#495057'
                                                },
                                                grid: {
                                                    color: '#ebedef'
                                                }
                                            },
                                            y: {
                                                ticks: {
                                                    color: '#495057'
                                                },
                                                grid: {
                                                    color: '#ebedef'
                                                }
                                            }
                                        }
                                    }} />
                                </Card>
                            </div>
                            <div className="col-12 lg:col-6">
                                <Card title="Product Preferences">
                                    <Chart type="pie" data={productPreferencesData} />
                                </Card>
                            </div>
                            <div className="col-12">
                                <Card title="Top Products">
                                    <DataTable
                                        value={selectedClient.topProducts}
                                        rows={5}
                                        emptyMessage="No product data available"
                                        responsiveLayout="scroll"
                                    >
                                        <Column field="productName" header="Product" />
                                        <Column field="quantity" header="Quantity" />
                                        <Column
                                            field="totalValue"
                                            header="Total Value"
                                            body={(rowData) => formatCurrency(rowData.totalValue)}
                                        />
                                    </DataTable>
                                </Card>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientOrderAnalysis;
